import { MASTER_PART_TYPE } from "./designerUtils";
import { sanitizeColorName } from "./designerUtils";
import {
    PRINT_NODE_TYPE,
    MODAL_DEPENDENT_TYPES,
    DEBUG_ALWAYS_SHOW_PART_MODAL,
    shouldHideDesignerPart,
    collectSubtreeKeys,
    dedupeNodesByKey,
    findFirstUsefulGroupLevel,
    isVisibleDesignerNode,
    hasDesignerNodeImages,
    isStripePartNode,
} from "./designerHelpers";

export const collectOptionRingColors = ({ subtreeKeys = [], selectedKeys, cardByKey }) => {
    const unique = new Map();
    subtreeKeys.forEach((nodeKey) => {
        const key = String(nodeKey || "");
        if (!key || !selectedKeys.has(key)) return;
        const selectedColor = cardByKey?.[key]?.selectedColor;
        if (!selectedColor) return;
        const colorCode = String(selectedColor.code || "").trim();
        if (!colorCode) return;
        const colorKey = String(
            selectedColor.color_id
            ?? selectedColor.id
            ?? selectedColor.color_chart_id
            ?? colorCode
        );
        if (!unique.has(colorKey)) {
            unique.set(colorKey, {
                key: colorKey,
                code: colorCode,
                name: sanitizeColorName(selectedColor.name, "Color"),
            });
        }
    });
    return Array.from(unique.values());
};

const buildGroupOptionRecursive = ({
    optionNode,
    cardByKey,
    selectedKeys,
    activePartKey,
    hideStripeParts = false,
}) => {
    if (!optionNode || !isVisibleDesignerNode(optionNode)) return null;
    if (hideStripeParts && isStripePartNode(optionNode)) return null;

    const key = String(optionNode.key);
    const optionCard = cardByKey[key];
    const subtreeKeysResult = collectSubtreeKeys(optionNode);
    const dependentTypeSet = MODAL_DEPENDENT_TYPES[Number(optionNode.type)] || null;
    const dependentOptions = dependentTypeSet
        ? dedupeNodesByKey((optionNode.children || []).filter(
            (child) => isVisibleDesignerNode(child) && dependentTypeSet.has(Number(child.type))
        ))
            .map((child) => buildGroupOptionRecursive({
                optionNode: child,
                cardByKey,
                selectedKeys,
                activePartKey,
                hideStripeParts,
            }))
            .filter(Boolean)
        : [];
    const ringColors = collectOptionRingColors({ subtreeKeys: subtreeKeysResult, selectedKeys, cardByKey });

    if (!DEBUG_ALWAYS_SHOW_PART_MODAL && !hasDesignerNodeImages(optionNode) && dependentOptions.length === 0) {
        return null;
    }

    return {
        key,
        label: optionCard?.label || optionNode?.Item?.nname || optionNode?.Item?.name || "Parte",
        iconUrl: optionNode?.Item?.img_icon || null,
        selected: optionCard?.selected ?? selectedKeys.has(key),
        complete: optionCard?.complete ?? true,
        missing: optionCard?.missing || [],
        isExclusiveVariant: optionCard?.isExclusiveVariant ?? false,
        isExclusiveRequiredGroup: optionCard?.isExclusiveRequiredGroup ?? false,
        required: optionCard?.required ?? !!optionNode?.required,
        materialOptions: optionCard?.materialOptions || [],
        selectedMaterialId: optionCard?.selectedMaterialId || null,
        selectedColor: optionCard?.selectedColor || null,
        ringColors,
        availableColors: optionCard?.availableColors || [],
        showMaterialSelector: !!optionCard?.showMaterialSelector,
        isAutoMaterialResolving: !!optionCard?.isAutoMaterialResolving,
        paletteLoaded: optionCard?.paletteLoaded || false,
        paletteLoading: optionCard?.paletteLoading || false,
        paletteOpen: !!optionCard?.paletteOpen,
        requiresManualMaterialSelection: !!optionCard?.requiresManualMaterialSelection,
        requiresManualColorSelection: !!optionCard?.requiresManualColorSelection,
        showMaterialFeedback: !!optionCard?.showMaterialFeedback,
        isLeftRightPart: !!optionCard?.isLeftRightPart,
        pairedPartKey: optionCard?.pairedPartKey || null,
        pairedPartLabel: optionCard?.pairedPartLabel || null,
        pairedPartSelected: !!optionCard?.pairedPartSelected,
        pairGroupKey: optionCard?.pairGroupKey || null,
        node: optionNode,
        subtreeKeys: subtreeKeysResult,
        active: subtreeKeysResult.includes(String(activePartKey || "")),
        dependentOptions,
        hasConfigurator: DEBUG_ALWAYS_SHOW_PART_MODAL
            || (optionCard?.materialOptions || []).length > 0
            || !!optionCard?.showMaterialFeedback
            || dependentOptions.length > 0,
        forceModalDebug: DEBUG_ALWAYS_SHOW_PART_MODAL,
    };
};

export { buildGroupOptionRecursive };

export const buildGroupOption = (props) => buildGroupOptionRecursive(props);

export const buildPartGroupsFromTree = ({
    root,
    selectedKeys,
    cardByKey,
    activePartKey,
    hideStripeParts = false,
}) => {
    if (!root) return [];

    const groups = [];
    const extras = [];
    const topLevelNodes = root.children || [];

    topLevelNodes.forEach((topNode) => {
        if (!topNode) return;

        const topType = Number(topNode.type);
        if (topType === PRINT_NODE_TYPE) return;

        const directPartChildren = dedupeNodesByKey(
            (topNode.children || []).filter(
                (child) =>
                    isVisibleDesignerNode(child)
                    && Number(child.type) === Number(MASTER_PART_TYPE.PART)
            )
        );
        const isVisibleTopNode = isVisibleDesignerNode(topNode);
        const allowHiddenPartAsSection =
            topType === Number(MASTER_PART_TYPE.PART) && directPartChildren.length > 0;
        if (!isVisibleTopNode && !allowHiddenPartAsSection) return;

        if (topType === Number(MASTER_PART_TYPE.EXTRA)) {
            extras.push(topNode);
            return;
        }

        if (topType !== Number(MASTER_PART_TYPE.PART)) return;

        const selectorChildren = findFirstUsefulGroupLevel(topNode);
        const groupLabel = topNode?.Item?.nname || topNode?.Item?.name || "Grupo";
        const optionNodes = directPartChildren.length > 0
            ? directPartChildren
            : dedupeNodesByKey(selectorChildren.length > 0 ? selectorChildren : [topNode]);
        const options = optionNodes
            .map((optionNode) => buildGroupOption({
                optionNode,
                cardByKey,
                selectedKeys,
                activePartKey,
                hideStripeParts,
            }))
            .filter((option) =>
                !!option
                && !shouldHideDesignerPart({
                    groupLabel,
                    partLabel: option.label,
                    node: option.node,
                })
            );

        if (!options.length) return;

        const activeOption = options.find((option) => option.active)
            || options.find((option) => option.selected)
            || options[0];

        groups.push({
            key: `group-${topNode.key}`,
            label: topNode?.Item?.nname || topNode?.Item?.name || "Grupo",
            sourceKey: String(topNode.key),
            synthetic: false,
            options,
            activeOptionKey: activeOption?.key || null,
            appliedColorChips: [],
        });
    });

    if (extras.length > 0) {
        const extraOptions = dedupeNodesByKey(extras)
            .map((extraNode) => buildGroupOption({
                optionNode: extraNode,
                cardByKey,
                selectedKeys,
                activePartKey,
                hideStripeParts,
            }))
            .filter(Boolean);

        const activeExtraOption = extraOptions.find((option) => option.active)
            || extraOptions.find((option) => option.selected)
            || extraOptions[0];

        groups.push({
            key: "group-extras",
            label: "Extras",
            sourceKey: "extras",
            synthetic: true,
            options: extraOptions,
            activeOptionKey: activeExtraOption?.key || null,
            appliedColorChips: [],
        });
    }

    return groups;
};
